import { Link, useNavigate } from 'react-router-dom';
import { Menu, User, LogOut, LayoutDashboard, X, Megaphone, ShoppingBag } from 'lucide-react'; // ✅ Added ShoppingBag icon
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useState } from 'react';

export const Header = () => {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const AKIT_LOGO = "https://customer-assets.emergentagent.com/job_dev-auth-platform/artifacts/3yr5z3x8_AKIT%20LOGO%202%281%29.png";

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/5 bg-black/50 backdrop-blur-xl supports-[backdrop-filter]:bg-black/20">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img 
              src={AKIT_LOGO} 
              alt="AKIT Logo" 
              className="h-12 w-12 rounded-lg object-contain"
            />
            <div className="hidden sm:block">
              <h1 className="text-lg font-heading font-bold text-foreground">AKIT</h1>
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Kenya IRE Teachers</p>
            </div>
          </Link>

          {/* Arabic Text */}
          <div className="hidden md:block text-center">
            <p className="text-primary text-sm font-amiri">بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيم</p>
            <p className="text-[10px] text-muted-foreground">Bismillahi Rahmani Rahim</p>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/resources" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-resources">
              Resources
            </Link>
            <Link to="/vacancies" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-vacancies">
              Jobs
            </Link>
            {/* ✅ NEW: Marketplace Link */}
            <Link to="/advertisements" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1" data-testid="nav-marketplace">
              <ShoppingBag className="h-3.5 w-3.5" />
              Marketplace
            </Link>
            <Link to="/advertise" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1" data-testid="nav-advertise">
              <Megaphone className="h-3.5 w-3.5" />
              Advertise
            </Link>
            <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-contact">
              Contact
            </Link>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full bg-primary/20" data-testid="user-menu-trigger">
                    <User className="h-5 w-5 text-primary" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="header-login">
                  Login
                </Button>
                <Button className="btn-primary" onClick={() => navigate('/register')} data-testid="header-register">
                  Join AKIT
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10 animate-fade-in">
            <div className="flex flex-col gap-2">
              <Link to="/resources" className="px-4 py-2 text-sm hover:bg-white/5 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Resources
              </Link>
              <Link to="/vacancies" className="px-4 py-2 text-sm hover:bg-white/5 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Jobs
              </Link>
              {/* ✅ NEW: Marketplace Link (Mobile) */}
              <Link to="/advertisements" className="px-4 py-2 text-sm hover:bg-white/5 rounded-lg flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <ShoppingBag className="h-4 w-4 text-primary" />
                Marketplace
              </Link>
              <Link to="/advertise" className="px-4 py-2 text-sm hover:bg-white/5 rounded-lg flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Megaphone className="h-4 w-4 text-primary" />
                Advertise
              </Link>
              <Link to="/contact" className="px-4 py-2 text-sm hover:bg-white/5 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                Contact
              </Link>
              {isAuthenticated ? (
                <>
                  <Link 
                    to={isAdmin ? '/admin' : '/dashboard'} 
                    className="px-4 py-2 text-sm hover:bg-white/5 rounded-lg"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }} 
                    className="px-4 py-2 text-sm text-destructive hover:bg-white/5 rounded-lg text-left"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-2 text-sm hover:bg-white/5 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                    Login
                  </Link>
                  <Link to="/register" className="px-4 py-2 text-sm text-primary font-medium hover:bg-white/5 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                    Join AKIT
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
